import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Maximize2 } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'

import { CreativeLightbox, downloadCreativeFile } from '@/components/chat/CreativeLightbox'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { GeneratingImageState, imageAspectRatio, imageTileMaxWidth } from './imageProgressState'

/** Caption clip: the tile names the image, it does not reprint the prompt. */
const SUMMARY_MAX = 80

export interface GeneratingImageTileProps {
  image: GeneratingImageState
  /** Short prompt summary for the caption; absent hides the caption. */
  summary?: string
}

/** Self-contained reduced-motion probe (same shape as `MorphText`). */
function usePrefersReducedMotion(): boolean {
  const [isReduced, setIsReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (!query || typeof query.addEventListener !== 'function') {
      return
    }
    const onChange = () => setIsReduced(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return isReduced
}

function clipSummary(summary: string | undefined): string | null {
  const text = (summary ?? '').replace(/\s+/g, ' ').trim()
  if (!text) {
    return null
  }
  return text.length > SUMMARY_MAX ? `${text.slice(0, SUMMARY_MAX - 1).trimEnd()}…` : text
}

type FailureCopyKey = 'threadImage.policyFailed' | 'threadImage.tooLong' | 'threadImage.failed'
  | 'threadImage.insufficientCredits' | 'threadImage.billingUnavailable' | 'threadImage.approval.contextUnavailable'

function failureKey(reason: string | undefined): FailureCopyKey {
  if (reason === 'insufficient_credits') return 'threadImage.insufficientCredits'
  if (reason === 'billing') return 'threadImage.billingUnavailable'
  if (reason === 'creative_inputs') return 'threadImage.approval.contextUnavailable'
  if (reason === 'policy') {
    return 'threadImage.policyFailed'
  }
  if (reason === 'timeout') {
    return 'threadImage.tooLong'
  }
  return 'threadImage.failed'
}

/**
 * One `generate_image` call in the thread (image plan, lifecycle table): a box
 * at the final aspect ratio while it renders, a blurred partial sharpening,
 * a 200ms crossfade to the final image, or a quiet note when it failed or
 * stopped. Reduced motion gets a static placeholder and no crossfade.
 */
export function GeneratingImageTile(props: GeneratingImageTileProps) {
  const { image, summary } = props

  const { t } = useTranslation('app')
  const [isFinalLoaded, setIsFinalLoaded] = useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  const isReducedMotion = usePrefersReducedMotion()

  const finalUrl = image.stage === 'done' ? image.asset?.ref.publicUrl : undefined
  const finalMime = image.asset?.ref.mimeType

  const onDownload = () => {
    if (!finalUrl) {
      return
    }
    downloadCreativeFile(finalUrl, finalMime).catch(() => {
      toast.error(t('library.preview.toastDownloadFailed'))
    })
  }

  const caption = clipSummary(summary)
  const testId = `thread-generating-image-${image.callId}`

  if (image.stage === 'failed' || image.stage === 'stopped') {
    const isFailed = image.stage === 'failed'
    return (
      <div
        className="flex w-full max-w-sm flex-col gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3"
        data-testid={testId}
        data-stage={image.stage}
      >
        <Text
          size="sm"
          color="muted"
          data-testid={isFailed ? `thread-generated-image-failed-${image.callId}` : undefined}
        >
          {isFailed ? t(failureKey(image.reason)) : t('threadImage.stopped')}
        </Text>
      </div>
    )
  }

  const isPending = image.stage === 'queued' || image.stage === 'rendering'
  const preview = image.previewDataUrl
  const shimmer =
    isPending && !isReducedMotion ? (
      <div
        aria-hidden
        data-testid={`${testId}-shimmer`}
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-muted-foreground/10 to-transparent animate-shimmer"
      />
    ) : null

  const previewImage = preview ? (
    <img
      src={preview}
      alt=""
      aria-hidden
      data-testid={`${testId}-preview`}
      className={cn(
        'absolute inset-0 h-full w-full scale-105 object-cover blur-md',
        !isReducedMotion && 'transition-opacity duration-200',
        isFinalLoaded && 'opacity-0',
      )}
    />
  ) : null

  const finalImage = finalUrl ? (
    <img
      src={finalUrl}
      alt={image.asset?.ref.altText || image.asset?.ref.label || ''}
      onLoad={() => setIsFinalLoaded(true)}
      data-testid={`${testId}-final`}
      className={cn(
        'absolute inset-0 h-full w-full object-cover',
        !isReducedMotion && 'transition-opacity duration-200',
        isFinalLoaded || isReducedMotion ? 'opacity-100' : 'opacity-0',
      )}
    />
  ) : null

  const actions = finalUrl ? (
    <div className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none">
      <button
        type="button"
        onClick={() => setIsLightboxOpen(true)}
        aria-label={t('chromeA11y.fullCreative')}
        data-testid={`${testId}-open`}
        className="rounded-full bg-background/90 p-1.5 text-foreground shadow-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Maximize2 className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDownload}
        aria-label={t('threadImage.download')}
        data-testid={`${testId}-download`}
        className="rounded-full bg-background/90 p-1.5 text-foreground shadow-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Download className="h-4 w-4" aria-hidden />
      </button>
    </div>
  ) : null

  const lightbox =
    isLightboxOpen && finalUrl ? (
      <CreativeLightbox
        creatives={[{ src: finalUrl, mimeType: finalMime }]}
        index={0}
        onIndex={() => {}}
        onClose={() => setIsLightboxOpen(false)}
      />
    ) : null

  const captionNode =
    caption && image.stage !== 'done' ? (
      <Text size="xs" color="muted" data-testid={`${testId}-caption`}>
        {t('threadImage.caption', { summary: caption })}
      </Text>
    ) : null

  return (
    <figure
      className="flex w-full flex-col gap-1.5 [--tile-max-h:20rem] sm:[--tile-max-h:24rem]"
      style={{ maxWidth: imageTileMaxWidth(image.size) }}
      data-testid={testId}
      data-stage={image.stage}
    >
      <div
        className="group relative w-full overflow-hidden rounded-xl bg-muted"
        style={{ aspectRatio: imageAspectRatio(image.size) }}
        aria-busy={image.stage !== 'done'}
      >
        {shimmer}
        {previewImage}
        {finalImage}
        {actions}
      </div>
      {captionNode ? <figcaption>{captionNode}</figcaption> : null}
      {lightbox}
    </figure>
  )
}
